/** Пути только относительные: baseURL уже несёт префикс, абсолютный путь удвоит его и даст 404. */

import { AxiosPromise, AxiosResponse } from 'axios';

import { fetchAuditorOptions } from './auditors';
import { mmHttp, MmRequestConfig } from './mmHttp';
import { MM_QUERY_PARAM_NAMES } from './pagination';
import {
  buildCreateContinuousTaskBody,
  buildCreateFreeTaskBody,
  buildCreateTpcTaskBody,
  buildStoresFilter,
  buildTaskStatusQuery,
  readCreatedTasks,
  readTasks,
} from './tasksWire';
import {
  MmCreateContinuousTaskRequest,
  MmCreateFreeTaskRequest,
  MmCreateKviTaskRequest,
  MmCreateTaskResponse,
  MmCreateTpcTaskRequest,
  MmPage,
  MmTaskFilters,
  MmTaskListFilters,
} from '../types/dto';
import { MmAuditor, MmTask } from '../types/domain';

const MONITORING_TASKS_PATH = '/monitoring/tasks';

export const MM_TASKS_LIVE_URLS = {
  list: MONITORING_TASKS_PATH + '/byStatuses',
  detail: MONITORING_TASKS_PATH,
  createContinuous: MONITORING_TASKS_PATH + '/createContinuousMonitoringTasks',
  createTpc: MONITORING_TASKS_PATH + '/createTpcMonitoringTasks',
  createFree: MONITORING_TASKS_PATH + '/createFreeMonitoringTasks',
  countByStoresFilter: MONITORING_TASKS_PATH + '/subtasks/count/byStoresFilter',
};

/** Меняем только `data`: смена типа результата тихо сломает вызывающих, компилятор не подскажет */
function toDomainCreateResponse(response: AxiosResponse<any>): AxiosResponse<MmCreateTaskResponse> {
  return {
    data: readCreatedTasks(response.data),
    status: response.status,
    statusText: response.statusText,
    headers: response.headers,
    config: response.config,
    request: response.request,
  };
}

/** Ручки создания KVI на бэкенде нет: вызов отклоняется сразу, адрес свободного не подходит */
export function createKviTask(
  payload: MmCreateKviTaskRequest,
  config?: MmRequestConfig,
): AxiosPromise<MmCreateTaskResponse> {
  return Promise.reject(new Error('Создание заданий типа KVI на бэкенде не реализовано')) as any;
}

export function createFreeTask(
  payload: MmCreateFreeTaskRequest,
  config?: MmRequestConfig,
): AxiosPromise<MmCreateTaskResponse> {
  return mmHttp
    .post(
      MM_TASKS_LIVE_URLS.createFree,
      buildCreateFreeTaskBody(payload),
      Object.assign({ mmSection: 'tasks/create/free' }, config),
    )
    .then(toDomainCreateResponse);
}

export function createTpcTask(
  payload: MmCreateTpcTaskRequest,
  config?: MmRequestConfig,
): AxiosPromise<MmCreateTaskResponse> {
  return mmHttp
    .post(
      MM_TASKS_LIVE_URLS.createTpc,
      buildCreateTpcTaskBody(payload),
      Object.assign({ mmSection: 'tasks/create/tpc' }, config),
    )
    .then(toDomainCreateResponse);
}

export function createContinuousTask(
  payload: MmCreateContinuousTaskRequest,
  config?: MmRequestConfig,
): AxiosPromise<MmCreateTaskResponse> {
  return mmHttp
    .post(
      MM_TASKS_LIVE_URLS.createContinuous,
      buildCreateContinuousTaskBody(payload),
      Object.assign({ mmSection: 'tasks/create/continuous' }, config),
    )
    .then(toDomainCreateResponse);
}

export function fetchZoneEstimate(
  filters: MmTaskFilters,
  config?: MmRequestConfig,
): AxiosPromise<number> {
  return mmHttp.post(
    MM_TASKS_LIVE_URLS.countByStoresFilter,
    buildStoresFilter(filters),
    Object.assign({ mmSection: 'tasks/estimate' }, config),
  );
}

/** Перечень двигать вместе с MM_TASK_LIST_CAPABILITIES: ключ здесь означает выключенный флаг там */
export const MM_TASKS_IGNORED_FILTER_KEYS: string[] = [
  'groups',
  'categories',
  'subcategories',
];

/** Бэкенд не сортирует вовсе: сортировка целиком клиентская и только по дошедшим до модели полям */
export const MM_TASKS_CLIENT_SORT_FIELDS: string[] = ['createdAt', 'type', 'status', 'competitorCode'];

function isFilterFilled(value: any): boolean {
  if (value === null || value === undefined) {
    return false;
  }

  if (Object.prototype.toString.call(value) === '[object Array]') {
    return value.length > 0;
  }

  if (typeof value === 'string') {
    return value !== '';
  }

  return true;
}

export function listIgnoredTaskFilters(filters: Partial<MmTaskListFilters>): string[] {
  const ignored: string[] = [];

  if (!filters) {
    return ignored;
  }

  const source = filters as { [key: string]: any };

  for (let i = 0; i < MM_TASKS_IGNORED_FILTER_KEYS.length; i += 1) {
    const key = MM_TASKS_IGNORED_FILTER_KEYS[i];

    if (isFilterFilled(source[key])) {
      ignored.push(key);
    }
  }

  return ignored;
}

/**
 * Поле сортировки, которое было запрошено, но выполнено быть не может,
 * либо `null`, если сортировка честная (в том числе если её нет вовсе).
 */
export function listIgnoredTaskSort(pageParams: { [key: string]: any }): string | null {
  if (!pageParams) {
    return null;
  }

  const field = pageParams[MM_QUERY_PARAM_NAMES.sortBy];

  if (typeof field !== 'string' || field === '') {
    return null;
  }

  return MM_TASKS_CLIENT_SORT_FIELDS.indexOf(field) === -1 ? field : null;
}

function readPageNumber(pageParams: { [key: string]: any }, name: string): number | null {
  if (!pageParams) {
    return null;
  }

  const value = pageParams[name];

  return typeof value === 'number' && isFinite(value) ? value : null;
}

/** Обе границы приводим к дате: сравнение YYYY-MM-DD с полным ISO теряло бы последний день. */
function readDay(value: string | null): string | null {
  if (typeof value !== 'string' || value === '') {
    return null;
  }

  return value.length >= 10 ? value.slice(0, 10) : value;
}

/** Поле приходит в фильтрах, но в типе не описано */
function readFilterString(filters: Partial<MmTaskListFilters>, name: string): string | null {
  if (!filters) {
    return null;
  }

  const value = (filters as { [key: string]: any })[name];

  return typeof value === 'string' && value !== '' ? value : null;
}

/** Пустой список — фильтр не задан; `0` и `-1` в селекте означают «Все», поэтому отбрасываются */
function readFilterIds(values: number[] | null | undefined): number[] {
  const ids: number[] = [];

  if (!values || values.length === 0) {
    return ids;
  }

  for (let i = 0; i < values.length; i += 1) {
    const value = Number(values[i]);

    if (!isNaN(value) && value > 0) {
      ids.push(value);
    }
  }

  return ids;
}

function readFilterCodes(values: string[] | null | undefined): string[] {
  const codes: string[] = [];

  if (!values || values.length === 0) {
    return codes;
  }

  for (let i = 0; i < values.length; i += 1) {
    const value = values[i];

    if (typeof value === 'string' && value !== '') {
      codes.push(value.toLowerCase());
    }
  }

  return codes;
}

function readFilterNames(values: string[] | null | undefined): string[] {
  const names: string[] = [];

  if (!values || values.length === 0) {
    return names;
  }

  for (let i = 0; i < values.length; i += 1) {
    const value = values[i];

    if (typeof value === 'string' && value.trim() !== '') {
      names.push(value.trim().toLowerCase());
    }
  }

  return names;
}

/** Сравниваем и по имени: числовой id зоны у строки штатно бывает null при заполненном названии */
function matchesZoneAxis(
  ids: number[],
  names: string[],
  taskId: number | null,
  taskName: string | null,
): boolean {
  if (ids.length === 0 && names.length === 0) {
    return true;
  }

  if (taskId !== null && taskId !== undefined && ids.indexOf(taskId) !== -1) {
    return true;
  }

  if (typeof taskName === 'string' && taskName.trim() !== '') {
    return names.indexOf(taskName.trim().toLowerCase()) !== -1;
  }

  return false;
}

function matchesCompetitorQuery(task: MmTask, query: string): boolean {
  const store = task.competitorStore;
  const haystack: (string | null)[] = [
    task.competitorCode,
    store ? store.name : null,
    store ? store.address : null,
  ];

  for (let i = 0; i < haystack.length; i += 1) {
    const value = haystack[i];

    if (value !== null && value !== undefined && String(value).toLowerCase().indexOf(query) !== -1) {
      return true;
    }
  }

  return false;
}

function applyClientTaskFilters(tasks: MmTask[], filters: Partial<MmTaskListFilters>): MmTask[] {
  if (!filters) {
    return tasks;
  }

  /** Проверяем статусы ещё раз: бэкенд может вернуть подзадачи и в чужих статусах */
  const statuses = readFilterCodes(filters.statuses);
  /** Регистр приводим с обеих сторон: у типов провод в верхнем регистре, у статусов домен в нижнем */
  const types = readFilterCodes(filters.types);
  const query = typeof filters.q === 'string' ? filters.q.trim().toLowerCase() : '';
  const competitorCodes = readFilterCodes(filters.competitorCodes);
  const batchId = readFilterString(filters, 'batchId');
  const createdFrom = readDay(readFilterString(filters, 'createdFrom'));
  const createdTo = readDay(readFilterString(filters, 'createdTo'));

  const hasPeriod = createdFrom !== null || createdTo !== null;

  const formats = readFilterIds(filters.formats);
  const regions = readFilterIds(filters.regions);
  const clusters = readFilterIds(filters.clusters);
  const formatNames = readFilterNames(filters.formatNames);
  const regionNames = readFilterNames(filters.regionNames);
  const clusterNames = readFilterNames(filters.clusterNames);
  const auditorLogins = readFilterCodes(filters.auditorLogins);

  if (
    statuses.length === 0 &&
    types.length === 0 &&
    query === '' &&
    competitorCodes.length === 0 &&
    batchId === null &&
    !hasPeriod &&
    formats.length === 0 &&
    regions.length === 0 &&
    clusters.length === 0 &&
    formatNames.length === 0 &&
    regionNames.length === 0 &&
    clusterNames.length === 0 &&
    auditorLogins.length === 0
  ) {
    return tasks;
  }

  const result: MmTask[] = [];

  for (let i = 0; i < tasks.length; i += 1) {
    const task = tasks[i];

    if (statuses.length > 0) {
      const status = typeof task.status === 'string' ? task.status.toLowerCase() : '';

      if (statuses.indexOf(status) === -1) {
        continue;
      }
    }

    if (types.length > 0) {
      /** Тип задания бэкенд может не прислать, разбор подставит CONTINUOUS — пачка попадёт в «Сплошной» */
      const type = typeof task.type === 'string' ? String(task.type).toLowerCase() : '';

      if (types.indexOf(type) === -1) {
        continue;
      }
    }

    if (query !== '' && !matchesCompetitorQuery(task, query)) {
      continue;
    }

    if (competitorCodes.length > 0) {
      const code = typeof task.competitorCode === 'string' ? task.competitorCode.toLowerCase() : '';

      if (competitorCodes.indexOf(code) === -1) {
        continue;
      }
    }

    if (batchId !== null && task.batchId !== batchId) {
      continue;
    }

    if (!matchesZoneAxis(formats, formatNames, task.storeFormatId, task.storeFormatName)) {
      continue;
    }

    if (!matchesZoneAxis(regions, regionNames, task.storeRegionId, task.storeRegionName)) {
      continue;
    }

    if (!matchesZoneAxis(clusters, clusterNames, task.storeClusterId, task.storeClusterName)) {
      continue;
    }

    if (auditorLogins.length > 0) {
      const login = typeof task.auditorLogin === 'string' ? task.auditorLogin.toLowerCase() : '';

      if (login === '' || auditorLogins.indexOf(login) === -1) {
        continue;
      }
    }

    if (hasPeriod) {
      const day = readDay(task.createdAt);

      if (day === null) {
        continue;
      }

      if (createdFrom !== null && day < createdFrom) {
        continue;
      }

      if (createdTo !== null && day > createdTo) {
        continue;
      }
    }

    result.push(task);
  }

  return result;
}

function compareText(left: string | null, right: string | null): number {
  const a = left === null || left === undefined ? '' : String(left);
  const b = right === null || right === undefined ? '' : String(right);

  if (a < b) {
    return -1;
  }

  return a > b ? 1 : 0;
}

/** Ветки здесь и ключи MM_TASKS_CLIENT_SORT_FIELDS правятся вместе; незнакомое поле даёт null */
function readSortValue(task: MmTask, field: string): string | null {
  if (field === 'createdAt') {
    return task.createdAt;
  }

  if (field === 'type') {
    return task.type === null || task.type === undefined ? null : String(task.type);
  }

  if (field === 'status') {
    return task.status === null || task.status === undefined ? null : String(task.status);
  }

  if (field === 'competitorCode') {
    return typeof task.competitorCode === 'string' ? task.competitorCode : null;
  }

  return null;
}

/** Вторичный ключ id обязателен: createdAt у всей пачки одинаков, иначе строки прыгают */
function applyClientTaskSorting(tasks: MmTask[], pageParams: { [key: string]: any }): MmTask[] {
  if (!pageParams) {
    return tasks;
  }

  const field = pageParams[MM_QUERY_PARAM_NAMES.sortBy];

  if (typeof field !== 'string' || MM_TASKS_CLIENT_SORT_FIELDS.indexOf(field) === -1) {
    return tasks;
  }

  const direction = pageParams[MM_QUERY_PARAM_NAMES.sortDirection] === 'desc' ? -1 : 1;
  const sorted = tasks.slice();

  sorted.sort((left, right) => {
    const primary = compareText(readSortValue(left, field), readSortValue(right, field));

    if (primary !== 0) {
      return primary * direction;
    }

    return compareText(left.id, right.id) * direction;
  });

  return sorted;
}

/** Режем страницу сами, иначе `total` перестанет означать «всего найдено»; без размера отдаём всё */
function sliceTaskPage(tasks: MmTask[], pageParams: { [key: string]: any }): MmTask[] {
  const size = readPageNumber(pageParams, MM_QUERY_PARAM_NAMES.size);

  if (size === null || size <= 0) {
    return tasks.slice();
  }

  const pageRaw = readPageNumber(pageParams, MM_QUERY_PARAM_NAMES.page);
  const page = pageRaw === null || pageRaw < 0 ? 0 : pageRaw;
  const from = page * size;

  return tasks.slice(from, from + size);
}

/** Сервер фильтрует только по статусам, остальное доделывается на клиенте внутри этой функции */
export function fetchTasks(
  tradingNetworkId: number,
  filters: Partial<MmTaskListFilters>,
  pageParams: { [key: string]: any },
  config?: MmRequestConfig,
): Promise<MmPage<MmTask>> {
  const statusQuery = buildTaskStatusQuery(filters ? filters.statuses : null);
  const params: { [key: string]: any } = {};

  if (statusQuery !== null) {
    params.status = statusQuery;
  }

  return mmHttp
    .get(MM_TASKS_LIVE_URLS.list, Object.assign({ params: params, mmSection: 'tasks/list' }, config))
    .then((response) => {
      const tasks = readTasks(response.data);

      /** Имена исполнителей подмешиваются до фильтрации: логин появляется в модели только здесь */
      return fillAuditorNames(tasks, config).then((named) => {
        const filtered = applyClientTaskFilters(named, filters);
        const sorted = applyClientTaskSorting(filtered, pageParams);

        return {
          content: sliceTaskPage(sorted, pageParams),
          totalElements: filtered.length,
          // Страница собрана из полной выдачи: число точное, hasMore сервер в этом пути не сообщает
          totalKnown: true,
          hasMore: null,
        };
      });
    });
}

function fillAuditorNames(tasks: MmTask[], config?: MmRequestConfig): Promise<MmTask[]> {
  let hasAssignee = false;

  for (let i = 0; i < tasks.length; i += 1) {
    const id = tasks[i].auditorId;

    if (id !== null && id !== undefined && String(id) !== '') {
      hasAssignee = true;
      break;
    }
  }

  if (!hasAssignee) {
    return Promise.resolve(tasks);
  }

  return fetchAuditorOptions(config)
    .then((response) => {
      const options = response && response.data ? response.data : [];
      const byId: { [key: string]: MmAuditor } = {};

      for (let i = 0; i < options.length; i += 1) {
        const option = options[i];

        if (option && option.id !== null && option.id !== undefined) {
          byId[String(option.id)] = option;
        }
      }

      for (let i = 0; i < tasks.length; i += 1) {
        const task = tasks[i];

        if (task.auditorId === null || task.auditorId === undefined || String(task.auditorId) === '') {
          continue;
        }

        const found = byId[String(task.auditorId)];

        if (found) {
          task.auditorLogin = typeof found.login === 'string' && found.login !== '' ? found.login : null;
          task.auditorFullName = typeof found.fullName === 'string' && found.fullName !== '' ? found.fullName : null;
        }
      }

      return tasks;
    })
    .catch(() => tasks);
}

export function fetchTask(
  taskId: string,
  _tradingNetworkId: number | null,
  config?: MmRequestConfig,
): Promise<AxiosResponse<MmTask>> {
  return mmHttp
    .get(
      MM_TASKS_LIVE_URLS.detail + '/' + encodeURIComponent(taskId),
      Object.assign({ mmSection: 'tasks/detail' }, config),
    )
    .then((response) => {
      const parsed = readTasks([response.data]);
      const task = parsed.length > 0 ? parsed[0] : null;

      if (task === null) {
        return Promise.reject(buildTaskNotFoundError(config));
      }

      /** Ответ карточки несёт только assigneeId, поэтому имена исполнителя доклеиваются отдельно */
      return fillAuditorNames([task], config).then((named) => ({
        data: named.length > 0 ? named[0] : task,
        status: response.status,
        statusText: response.statusText,
        headers: response.headers,
        config: response.config,
        request: response.request,
      }) as AxiosResponse<MmTask>);
    });
}

/** Ошибку собираем с полем response: без него она будет разобрана как сетевая */
function buildTaskNotFoundError(config?: MmRequestConfig): Error {
  const error: any = new Error('Task not found');

  error.config = config ? config : {};
  error.response = {
    status: 404,
    statusText: 'Not Found',
    headers: {},
    data: { code: 'mm_not_found', message: 'Задание не найдено' },
  };

  return error;
}
